import { Outlet, useSearchParams } from 'react-router';
import { FullBox } from '@halo/ui-react';
import {
  GroupMemberJoinMode,
  isSessionCategory,
  SessionCategoryPrefix,
  useAppStore,
} from '@halo/chat-sdk-react';
import { useSettingsStore } from './store/settings';
import { useContactsStore } from './store/contacts';
import Paths from './configs/router/paths';
import { router } from './scripts/router';
import UpdateBar from './components/UpdateBar';
import NetOffline from './components/NetOffline';
import { useOpenLink } from './scripts/hooks';

function App() {
  const { setSyncAndBackupContacts, setSyncAndBackupWallet } = useSettingsStore();
  const { client, initTrigger, winUrl, winPromise, joinSession } = useAppStore();
  const { getContacts } = useContactsStore();
  const [searchParams] = useSearchParams();
  const chat_id_like = useMemo(() => searchParams.get('chat_id'), [searchParams]);
  const [offline, setOffline] = useState(false);

  useLayoutEffect(() => {
    winPromise.then((disabled) => {
      if (disabled) {
        const urlParams = new URLSearchParams(window.location.search);
        const chat_id = urlParams.get('chat_id');
        const isSingle = isSessionCategory(chat_id ?? '', SessionCategoryPrefix.Single);

        router.navigate(
          `${Paths.KICK_OUT}${chat_id ? `?join=${isSingle ? 'single' : 'group'}` : ''}`
        );
      } else if (window.location.pathname === Paths.KICK_OUT) {
        router.navigate(Paths.MESSAGES_CHATS);
      }
    });
  }, [winPromise]);

  const { openLink } = useOpenLink();

  useLayoutEffect(() => {
    if (winUrl) {
      if (winUrl.searchData.chat_id) {
        router.navigate(window.location.pathname + winUrl.search);
      } else {
        // compatibility
        const url = new URL(location.href);
        url.pathname = winUrl.pathname;
        url.search = winUrl.search;
        url.hash = winUrl.hash;
        openLink(url.toString());
      }
    }
  }, [winUrl, openLink]);

  useLayoutEffect(() => {
    if (chat_id_like) {
      winPromise.then((disabled) => {
        if (!disabled) {
          joinSession(chat_id_like, GroupMemberJoinMode.Link).then(({ chat_id }) => {
            router.navigate(`${Paths.MESSAGES_CHATS}/${chat_id}`);
          });
        }
      });
    }
  }, [chat_id_like, winPromise, joinSession]);

  useLayoutEffect(() => {
    if (client && initTrigger) {
      (async () => {
        const backupContacts = await client.Api.user.getUserData('sync_and_backup_contacts');
        const backupWallet = await client.Api.user.getUserData('sync_and_backup_wallet');
        setSyncAndBackupContacts(backupContacts != '2');
        setSyncAndBackupWallet(backupWallet != '2');
      })();
    }
  }, [client, initTrigger, setSyncAndBackupContacts, setSyncAndBackupWallet]);

  useEffect(() => {
    if (client && initTrigger) {
      getContacts(client);
    }
  }, [client, initTrigger, getContacts]);

  const onNetChangeHandler = useCallback((val: boolean) => {
    setOffline(val);
  }, []);
  const onlineHandler = useCallback(() => onNetChangeHandler(false), [onNetChangeHandler]);
  const offlineHandler = useCallback(() => onNetChangeHandler(true), [onNetChangeHandler]);
  useEffect(() => {
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);

    return () => {
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, []);

  return (
    <FullBox.Root id="app" column h="100dvh">
      <FullBox.Aside>
        <NetOffline offline={offline} />
        <UpdateBar></UpdateBar>
      </FullBox.Aside>
      <FullBox.Main>
        <Outlet></Outlet>
      </FullBox.Main>
    </FullBox.Root>
  );
}

export default App;
